import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_LABELS,
  type SupportedLanguage,
  coerceLanguage,
} from "../i18n";

/**
 * Compact language switcher used in the top nav.
 * Writes to the same localStorage key as the i18n bootstrap so the choice
 * persists across reloads.
 *
 * Touch target: the wrapping label is min-h-11 (44px) on mobile so the
 * underlying <select> is comfortable to tap, even though the visible
 * control is smaller. On desktop (md+) it shrinks to min-h-9 to match
 * the surrounding nav row.
 */
export function LocaleSwitcher() {
  const { i18n, t } = useTranslation();
  const current = coerceLanguage(i18n.language);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as SupportedLanguage;
    void i18n.changeLanguage(next);
  };

  return (
    <label className="inline-flex h-11 min-w-0 items-center gap-1.5 rounded-[var(--radius-md)] px-2 text-[12.5px] text-[var(--color-muted)] transition-colors hover:bg-[var(--color-panel-subtle)] hover:text-[var(--color-ink)] md:h-9 md:px-0">
      <Globe className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sr-only">{t("language.label")}</span>
      <select
        value={current}
        onChange={handleChange}
        className="cursor-pointer appearance-none border-0 bg-transparent pr-1 text-[12.5px] font-medium text-[var(--color-ink)] outline-none focus:ring-0"
        aria-label={t("language.label")}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <option key={lng} value={lng}>
            {LANGUAGE_LABELS[lng]}
          </option>
        ))}
      </select>
    </label>
  );
}
